import { normaliseLocale } from '~/lib/localization/locale';

/**
 * Strips a leading locale prefix from `pathname` — regardless of whether the
 * URL uses a bare code (`/da`), a canonical one (`/da-DK`), or any other
 * recognised variant — as long as it canonicalises to one of the available
 * storefront languages. Returns the path with that prefix removed, or the
 * original path if no recognised prefix is present.
 */
export function stripLocalePrefix(
  pathname: string,
  availableLanguages: string[]
): string {
  const firstSegment = pathname.split('/')[1];
  if (!firstSegment) return pathname;

  const canonicalSegment = normaliseLocale(firstSegment);
  if (!canonicalSegment) return pathname;

  const matches = availableLanguages.some(
    (l) => normaliseLocale(l) === canonicalSegment
  );
  if (!matches) return pathname;

  const stripped = pathname.slice(firstSegment.length + 1);
  return stripped.startsWith('/') ? stripped : `/${stripped}`;
}

/**
 * Builds the URL to navigate to when the user picks `targetLocale`:
 * - strips the current URL's locale prefix (if any);
 * - emits no prefix when `targetLocale` is the default language;
 * - otherwise prepends the (raw) target locale.
 */
export function buildLocaleUrl(
  pathname: string,
  targetLocale: string,
  availableLanguages: string[],
  defaultLanguage: string | undefined
): string {
  const basePath = stripLocalePrefix(pathname, availableLanguages);

  if (targetLocale === defaultLanguage) {
    return basePath === '' ? '/' : basePath;
  }

  return `/${targetLocale}${basePath === '/' ? '' : basePath}`;
}
